import React, { useState, useEffect } from 'react';
import Familiarization from './Familiarization';
import Trial from './Trial';
import { generateArtistBlock } from '../data/trials';
import { aiModels } from '../data/models';

function ArtistBlock({ artist, blockNumber, totalBlocks, participantID, onComplete, reviewerNav, isFirstBlock = false, onPhaseChange, onTrialIndexChange }) {
  const [phase, setPhase] = useState('intro');
  const [trials, setTrials] = useState([]);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(0);
  const [blockData, setBlockData] = useState({
    artist: artist.id,
    artistName: artist.name,
    blockNumber,
    trials: []
  });
  
  // Use reviewer navigation if available, otherwise use normal flow
  const displayPhase = reviewerNav?.blockPhase || phase;
  const displayTrialIndex = reviewerNav?.trialIndex !== null && reviewerNav?.trialIndex !== undefined ? reviewerNav.trialIndex : currentTrialIndex;

  // Sync internal state when navigation mode changes
  useEffect(() => {
    // When reviewerNav is provided and has navigation info, we're in nav mode
    // When it's cleared (blockPhase is null), we've exited nav mode
    if (reviewerNav?.blockPhase) {
      // In navigation mode - update internal state to match navigation position
      if (reviewerNav.blockPhase === 'familiarization') {
        setPhase('familiarization');
      } else if (reviewerNav.blockPhase === 'trials') {
        setPhase('trials');
        if (reviewerNav.trialIndex !== null && reviewerNav.trialIndex !== undefined) {
          setCurrentTrialIndex(reviewerNav.trialIndex);
        }
      } else if (reviewerNav.blockPhase === 'intro') {
        setPhase('intro');
      }
    }
  }, [reviewerNav]);

  useEffect(() => {
    // Generate complete block with practice, catch, and repeated trials
    const blockTrials = generateArtistBlock(artist, aiModels, isFirstBlock);
    setTrials(blockTrials);
  }, [artist, isFirstBlock]);

  useEffect(() => {
    // Auto-advance intro screen after 3 seconds (unless reviewer is navigating)
    if (!reviewerNav?.blockPhase && phase === 'intro') {
      const timer = setTimeout(() => {
        setPhase('familiarization');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [phase, reviewerNav?.blockPhase]);

  // Report phase changes to parent
  useEffect(() => {
    if (onPhaseChange && !reviewerNav?.blockPhase) {
      onPhaseChange(phase);
    }
  }, [phase, onPhaseChange, reviewerNav?.blockPhase]);

  // Report trial index changes to parent
  useEffect(() => {
    if (onTrialIndexChange && !reviewerNav?.blockPhase) {
      onTrialIndexChange(currentTrialIndex);
    }
  }, [currentTrialIndex, onTrialIndexChange, reviewerNav?.blockPhase]);

  const handleFamiliarizationComplete = () => {
    setPhase('trials');
    setCurrentTrialIndex(0);
  };

  const handleTrialComplete = (trialData) => {
    const updatedTrials = [...blockData.trials, trialData];
    setBlockData(prev => ({
      ...prev,
      trials: updatedTrials
    }));

    if (currentTrialIndex < trials.length - 1) {
      setCurrentTrialIndex(prev => prev + 1);
    } else {
      // Block complete
      onComplete({
        ...blockData,
        trials: updatedTrials
      });
    }
  };

  return (
    <div className="artist-block">
      {displayPhase === 'intro' && (
        <div className="block-intro">
          <p style={{ fontSize: '16px', marginBottom: '8px', opacity: 0.9 }}>
            Block {blockNumber} of {totalBlocks}
          </p>
          <h1>The next set of trials will be about the artist:</h1>
          <h1 style={{ fontSize: '48px', marginTop: '24px' }}>{artist.name}</h1>
          <p style={{ fontSize: '18px', marginTop: '16px', opacity: 0.9 }}>
            {artist.period}
          </p>
        </div>
      )}

      {displayPhase === 'familiarization' && (
        <Familiarization
          artist={artist}
          onComplete={handleFamiliarizationComplete}
          skipTimer={!!reviewerNav?.blockPhase}
        />
      )}

      {displayPhase === 'trials' && trials.length > 0 && (
        <>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((displayTrialIndex + 1) / trials.length) * 100}%` }}
            />
          </div>
          <Trial
            trial={trials[displayTrialIndex]}
            trialNumber={displayTrialIndex + 1}
            totalTrials={trials.length}
            artistName={artist.name}
            onComplete={handleTrialComplete}
            skipTimers={!!reviewerNav?.blockPhase}
          />
        </>
      )}
    </div>
  );
}

export default ArtistBlock;